import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import StackDrawer from "@/components/ui/stack-drawer";
import { stripeSupportedCountries } from "@/data/countries";
import { ClientsKeys, createClient } from "@/features/clients/api/clients";
import { Client } from "@/features/clients/types/clients";
import { track } from "@/utils/mixpanel";

const clientFormSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  company: z.string().min(1, "Company is required"),
  taxId: z.string().optional(),
  address: z.object({
    line1: z.string().min(1, "Address line 1 is required"),
    line2: z.string().optional(),
    city: z.string().min(1, "City is required"),
    state: z.string().optional(),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.string().min(1, "Country is required"),
  }),
});

type ClientFormValues = z.infer<typeof clientFormSchema>;

interface AddClientSheetProps {
  isOpen: boolean;
  onClose: () => void;
}

const AddClientSheet = ({ isOpen, onClose }: AddClientSheetProps) => {
  const queryClient = useQueryClient();

  const form = useForm<ClientFormValues>({
    resolver: zodResolver(clientFormSchema),
    defaultValues: {
      name: "",
      email: "",
      company: "",
      taxId: "",
      address: {
        line1: "",
        line2: "",
        city: "",
        state: "",
        postalCode: "",
        country: "",
      },
    },
  });

  const { mutate: createClientMutation, isPending } = useMutation({
    mutationFn: (client: Partial<Client>) => createClient(client),
    onSuccess: (data) => {
      toast.success("Client added successfully");
      track("client_created", {
        client_id: data.id,
        email: data.email,
        company: data.company,
        country: data.address?.country,
      });
      queryClient.invalidateQueries({ queryKey: [ClientsKeys.Clients] });
      form.reset();
      onClose();
    },
    onError: (error) => {
      console.error("Error adding client:", error);
      toast.error("Failed to add client. Please try again.");
    },
  });

  const onSubmit = (data: ClientFormValues) => {
    createClientMutation({
      name: data.name,
      email: data.email,
      company: data.company,
      taxId: data.taxId,
      address: {
        line1: data.address.line1,
        line2: data.address.line2,
        city: data.address.city,
        state: data.address.state,
        postalCode: data.address.postalCode,
        country: data.address.country,
      },
    });
  };

  const footerContent = (
    <div className="flex gap-2 justify-end">
      <Button
        type="button"
        variant="outline"
        onClick={onClose}
        disabled={isPending}
      >
        Cancel
      </Button>
      <Button type="submit" form="client-form" disabled={isPending}>
        {isPending ? "Adding..." : "Add Client"}
      </Button>
    </div>
  );

  return (
    <StackDrawer
      open={isOpen}
      onOpenChange={onClose}
      title="Add New Client"
      subtitle="Enter the details of the new client below."
      footer={footerContent}
    >
      <Form {...form}>
        <form
          id="client-form"
          onSubmit={form.handleSubmit(onSubmit)}
          className="space-y-4"
        >
          <FormField
            control={form.control}
            name="name"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Name <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="John Doe" autoComplete="name" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="email"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Email <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input
                    type="email"
                    placeholder="john@example.com"
                    autoComplete="email"
                    {...field}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="company"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Company <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="Acme Inc." autoComplete="organization" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="taxId"
            render={({ field }) => (
              <FormItem>
                <FormLabel>EIN / Tax ID</FormLabel>
                <FormControl>
                  <Input
                    placeholder="XX-XXXXXXX"
                    {...field}
                  />
                </FormControl>
                <p className="text-xs text-muted-foreground">
                  Appears on invoices sent to this client. Can be added later
                  but cannot be changed once set.
                </p>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address.line1"
            render={({ field }) => (
              <FormItem>
                <FormLabel>
                  Address Line 1 <span className="text-destructive">*</span>
                </FormLabel>
                <FormControl>
                  <Input placeholder="123 Main St" autoComplete="address-line1" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="address.line2"
            render={({ field }) => (
              <FormItem>
                <FormLabel>Address Line 2</FormLabel>
                <FormControl>
                  <Input placeholder="Suite 100" autoComplete="address-line2" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="address.city"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    City <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="New York" autoComplete="address-level2" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address.state"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>State</FormLabel>
                  <FormControl>
                    <Input placeholder="NY" autoComplete="address-level1" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <FormField
              control={form.control}
              name="address.postalCode"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Postal Code <span className="text-destructive">*</span>
                  </FormLabel>
                  <FormControl>
                    <Input placeholder="10001" autoComplete="postal-code" {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="address.country"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>
                    Country <span className="text-destructive">*</span>
                  </FormLabel>
                  <Select
                    onValueChange={field.onChange}
                    defaultValue={field.value}
                  >
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Select a country" />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {stripeSupportedCountries.map((country) => (
                        <SelectItem key={country.code} value={country.code}>
                          {country.country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />
          </div>
        </form>
      </Form>
    </StackDrawer>
  );
};

export default AddClientSheet;
